import React, { useMemo } from 'react';
import { createUseStyles } from 'react-jss';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { layoutDefaults } from '../../utils/layout-defaults';
import {
  parseMapPostalCode,
  parseMapRoute,
  parseMapStreetNumber,
} from '../../utils/manager/map-manager';

const mapHeight = 120;
const mapRenderHeight = 80;

function AddressCard({
  showMarker = true,
  showMap = true,
  className,
  address,
  placeDetails,
  baseLocation,
  noStreetNumber,
  complement,
  config,
  selectable = false,
  onClick,
}) {
  const { t } = useTranslation();
  const classes = useStyles({ selectable, showMarker });

  const mapUrl = useMemo(() => {
    let latitude;
    let longitude;

    if (address != null) {
      latitude = address?.coordinate?.latitude;
      longitude = address?.coordinate?.longitude;
    } else {
      const location = placeDetails?.geometry?.location;

      latitude = location?.lat();
      longitude = location?.lng();
    }

    const center = `${latitude},${longitude}`;
    const zoom = 16;
    const markerColor = layoutDefaults.destructiveColor;

    let markers = '';
    if (showMarker) {
      markers = `&markers=color:${markerColor}%7C${center}`;
    }

    return `https://maps.googleapis.com/maps/api/staticmap?center=${center}&zoom=${zoom}&size=${mapHeight}x${mapHeight}&key=${config?.maps?.browser?.api_key}${markers}`;
  }, [config, address, placeDetails, selectable, showMarker]);

  const route = useMemo(
    () => (address != null ? address.route : parseMapRoute(placeDetails)),
    [address, placeDetails]
  );

  const streetNumber = useMemo(
    () =>
      address != null ? address.number : parseMapStreetNumber(placeDetails),
    [address, placeDetails]
  );

  const postalCode = useMemo(
    () =>
      address != null ? address.postal_code : parseMapPostalCode(placeDetails),
    [address, placeDetails]
  );

  const baseLocationComponents = useMemo(() => {
    const separator = ', ';
    const location = address ?? baseLocation;

    let result =
      `${location.neighborhood?.name ?? ''}${separator}${location.city?.name ?? ''}${separator}${location.state?.code ?? ''}`.replace(
        /(, )+/g,
        separator
      );

    if (result.startsWith(separator)) {
      result = result.substring(separator.length);
    }

    return result;
  }, [address, baseLocation]);

  return (
    <div
      className={classNames(classes.container, className)}
      onClick={onClick}
      aria-hidden
    >
      {showMap && (
        <div className={classes.mapContainer}>
          <img alt="map" src={mapUrl} className={classes.map} />
        </div>
      )}
      <div className={classes.textContainer}>
        <div className={classes.routeContainer}>
          {!_.isEmpty(route) && <span>{route}, </span>}
          {_.isEmpty(route) && (
            <span className={classes.empty}>
              {t('address-card.route.empty')},{' '}
            </span>
          )}
          {!_.isEmpty(streetNumber) && <span>{streetNumber}</span>}
          {_.isEmpty(streetNumber) && noStreetNumber !== true && (
            <span className={classes.empty}>
              {t('address-card.street-number.empty')}
            </span>
          )}

          {_.isEmpty(streetNumber) && noStreetNumber === true && (
            <span>{t('address-card.no-street-number.term')}</span>
          )}

          {_.isEmpty(postalCode) && (
            <div className={classes.empty}>
              {t('address-card.postal-code.empty')}
            </div>
          )}
        </div>

        {/* {!_.isEmpty(baseLocationComponents) && (
          <div className={classes.baseComponents}>
            {baseLocation.neighborhood?.name}
          </div>
        )} */}

        <div className={classes.baseComponents}>{baseLocationComponents}</div>

        {!_.isEmpty(baseLocationComponents) && (
          <div className={classes.baseComponents}>{postalCode}</div>
        )}

        {!_.isEmpty(complement) && (
          <div className={classes.complement}>{complement}</div>
        )}
      </div>
    </div>
  );
}

const useStyles = createUseStyles({
  container: {
    display: 'flex',
    overflow: 'hidden',
    background: layoutDefaults.cardColor,

    paddingTop: layoutDefaults.smallVerticalMargin,
    paddingBottom: layoutDefaults.smallVerticalMargin,
    paddingLeft: layoutDefaults.horizontalMargin,
    paddingRight: layoutDefaults.horizontalMargin,

    borderRadius: layoutDefaults.radius,
    cursor: 'pointer',
    // border: `1px solid red`,
  },
  mapContainer: {
    display: 'flex',
    // flexDirection: 'row',
    // justifyContent: 'center',
    // alignItems: 'center',
    // flex: 1,
    // flexGrow: 1,
    // width: mapRenderHeight,
    // height: mapRenderHeight,
    opacity: ({ selectable, showMarker }) =>
      !selectable && showMarker ? layoutDefaults.opacity : 1,

    marginRight: layoutDefaults.horizontalMargin,
    // border: `1px solid green`,
  },
  map: {
    height: mapRenderHeight,
    width: mapRenderHeight,

    // border: `1px solid red`,
  },
  textContainer: {
    display: 'flex',
    flexDirection: 'column',
    // flex: 1,
    justifyContent: 'center',
    // alignItems: 'center',
    fontSize: layoutDefaults.fontSize,
    textAlign: 'left',
    // border: `1px solid blue`,
  },
  routeContainer: {
    textTransform: 'uppercase',
    fontSize: layoutDefaults.smallFontSize,
    // opacity: ({ selectable }) => (selectable ? 1 : layoutDefaults.opacity),
    fontWeight: ({ showName }) => (showName ? 'normal' : 'bold'),
    // border: `1px solid red`,
  },
  baseComponents: {
    // opacity: ({ selectable }) => (selectable ? 1 : layoutDefaults.opacity),
    // border: `1px solid red`,
  },
  complement: {
    // fontSize: layoutDefaults.smallFontSize,
    marginTop: layoutDefaults.smallerVerticalMargin,
    opacity: layoutDefaults.opacity,
  },
  empty: {
    color: layoutDefaults.destructiveColor,
  },
});

export { AddressCard };
